import { modalAnatomy as parts } from "@chakra-ui/anatomy";
import { extendTheme, ThemeConfig, useColorModeValue } from "@chakra-ui/react";
import { createMultiStyleConfigHelpers } from "@chakra-ui/styled-system";
import * as brand from "@intentsify/brand";
import { isDefined, shuffle } from "@intentsify/utils";

// https://chakra-ui.com/docs/components/modal/theming#adding-a-custom-variant
// eslint-disable-next-line @typescript-eslint/unbound-method
const { definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

// Palette names predate the intentsify.io restyle; they are kept so existing
// `brand.400`, `teal.500` etc. references pick up the brand ramps.
export const colors = {
  gray: brand.neutral,
  /** Dark surfaces (sidebar, header, dark-mode pages). */
  brand: brand.night,
  /** Primary brand green. */
  teal: brand.green,
  green: brand.lime,
  red: brand.red,
  orange: brand.orange,
  yellow: brand.gold,
  blue: brand.blue,
  pink: brand.magenta,
  night: brand.night,
  neutral: brand.neutral,
  primary: brand.green,
  lime: brand.lime,
  magenta: brand.magenta,
  gold: brand.gold,
  violet: brand.violet,
  purple: brand.violet,
};

export type Colour =
  | "blue"
  | "teal"
  | "green"
  | "yellow"
  | "orange"
  | "pink"
  | "red";

type ModifiableColour = Colour & { __type: "ModifiedColour" };
export type AnyColour = ModifiableColour | Colour;

export const colours: Colour[] = [
  "blue",
  "teal",
  "green",
  "yellow",
  "orange",
  "pink",
  "red",
];

export const isModifiableColour = (
  colour: AnyColour
): colour is ModifiableColour => {
  const modifiableRe = new RegExp(`^(${colours.join("|")})\\.\\d00$`);
  return modifiableRe.test(colour);
};
export const parseModifiableColour = (
  anyColour: AnyColour
): [Colour, number] => {
  if (!isModifiableColour(anyColour)) {
    return [anyColour, 0];
  }
  const [colour, modifier] = anyColour.split(".");
  return [colour as Colour, Number(modifier)];
};

export const getChartColours = (
  theme: "light" | "dark",
  pallete: Colour[] = colours,
  shuffleColors = true,
  includeShades = true
) => {
  const pool = pallete
    .map((p) => {
      return Object.entries(colors).find((c) => c[0] === p);
    })
    .filter(isDefined);

  const onlyColors = pool
    .map((c) => c[1])
    .map((c) => Object.entries(c))
    .flat()
    .filter((c) => {
      if (includeShades) {
        if (theme === "dark") {
          return Number(c[0]) <= 300;
        }

        if (theme === "light") {
          return Number(c[0]) >= 500;
        }
      }

      if (theme === "dark") {
        return Number(c[0]) === 300;
      }

      if (theme === "light") {
        return Number(c[0]) === 500;
      }
    })
    .map((c) => c[1]);

  if (shuffleColors) {
    return shuffle([
      ...onlyColors,
      ...onlyColors,
      ...onlyColors,
      ...onlyColors,
      ...onlyColors,
    ]);
  }

  return [
    ...onlyColors,
    ...onlyColors,
    ...onlyColors,
    ...onlyColors,
    ...onlyColors,
  ];
};

const styles = {
  global: {
    ".body": {
      height: "100%",
    },
    // Thin, rounded scrollbars that stay quiet until hovered. Chrome and
    // Firefox use the standard properties; Safari the -webkit ones.
    "*": {
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(110, 116, 114, 0.4) transparent",
    },
    "*::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
      backgroundColor: "transparent",
    },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(110, 116, 114, 0.4)",
      borderRadius: "999px",
      border: "3px solid transparent",
      backgroundClip: "content-box",
    },
    "*::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(110, 116, 114, 0.7)",
    },
    "*::-webkit-scrollbar-corner": {
      backgroundColor: "transparent",
    },
    ".chakra-ui-dark .notification-content a": {
      color: "teal.300",
      fontWeight: "500",
    },
    ".chakra-ui-light .notification-content a": {
      color: "teal.900",
      fontWeight: "500",
    },
  },
};

const tableDensity = {
  regular: { th: { py: 3, px: 4 }, td: { py: 3, px: 4 } },
  compact: { th: { py: 2, px: 3 }, td: { py: 2, px: 3 } },
};

const tableVariant =
  (
    density: keyof typeof tableDensity,
    { flush = false }: { flush?: boolean } = {},
  ) =>
  ({ colorMode }: { colorMode: "light" | "dark" }) => {
    const light = colorMode === "light";
    const border = `1px solid ${light ? colors.gray[100] : "#2C3230"}`;
    const padding = tableDensity[density];

    return {
      th: {
        ...padding.th,
        ...(flush ? { px: 0 } : {}),
        bg: flush ? "transparent" : light ? "gray.50" : "brand.400",
        color: light ? "gray.500" : "gray.400",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        borderBottom: border,
        // Rounded header ends on every table, whether or not its wrapper clips.
        ...(flush
          ? {}
          : {
              _first: { borderTopLeftRadius: "md" },
              _last: { borderTopRightRadius: "md" },
            }),
      },
      td: {
        ...padding.td,
        ...(flush ? { px: 0 } : {}),
        color: light ? "gray.900" : "gray.50",
        borderBottom: border,
      },
      tbody: {
        tr: {
          bg: "transparent",
          transition: "background-color 120ms ease",
          _hover: { bg: light ? "gray.25" : "whiteAlpha.50" },
          _last: { td: { borderBottom: "none" } },
        },
      },
    };
  };

const config: ThemeConfig = {
  initialColorMode: "system",
  useSystemColorMode: false,
};

const fonts = {
  heading: brand.fonts.stack,
  body: brand.fonts.stack,
};

const radii = {
  sm: brand.radii.sm,
  md: "8px",
  lg: brand.radii.md,
  xl: brand.radii.lg,
  "2xl": brand.radii.xl,
};

const semanticTokens = {
  colors: {
    "chakra-body-bg": { _light: "white", _dark: "brand.375" },
    "chakra-body-text": { _light: "gray.900", _dark: "gray.50" },
    "chakra-border-color": { _light: "gray.100", _dark: "#2C3230" },
    "chakra-placeholder-color": { _light: "gray.500", _dark: "gray.400" },
    "chakra-subtle-bg": { _light: "gray.50", _dark: "brand.300" },
    "chakra-subtle-text": { _light: "gray.600", _dark: "gray.300" },
  },
};

const brandColorSchemeDefaults = { defaultProps: { colorScheme: "teal" } };
const brandFocusBorder = { defaultProps: { focusBorderColor: "teal.500" } };

export const intentsifyTheme = extendTheme({
  colors,
  fonts,
  radii,
  semanticTokens,
  styles,
  config,
  components: {
    Heading: {
      baseStyle: {
        fontWeight: 500,
        letterSpacing: "-0.01em",
      },
    },
    Link: {
      baseStyle: {
        color: "teal.600",
        _dark: { color: "teal.300" },
      },
    },
    Checkbox: brandColorSchemeDefaults,
    Radio: brandColorSchemeDefaults,
    Switch: brandColorSchemeDefaults,
    Slider: brandColorSchemeDefaults,
    Progress: brandColorSchemeDefaults,
    Input: brandFocusBorder,
    NumberInput: brandFocusBorder,
    Select: brandFocusBorder,
    Textarea: brandFocusBorder,
    PinInput: brandFocusBorder,
    Button: {
      baseStyle: {
        fontWeight: 500,
      },
      variants: {
        // Chakra's teal solid puts white text on mint (3.1:1); use the darker
        // brand green on light and dark text on mint in dark mode instead.
        solid: ({ colorScheme }: { colorScheme: string }) =>
          colorScheme === "teal"
            ? {
                bg: "teal.600",
                color: "white",
                _hover: { bg: "teal.700", _disabled: { bg: "teal.600" } },
                _active: { bg: "teal.800" },
                _dark: {
                  bg: "teal.500",
                  color: "brand.450",
                  _hover: { bg: "teal.400", _disabled: { bg: "teal.500" } },
                  _active: { bg: "teal.300" },
                },
              }
            : {},
        brand: {
          bgImage: brand.linearGradient(brand.gradientStops.cta),
          color: "white",
          textShadow: "0 1px 1px rgba(0, 0, 0, 0.15)",
          _hover: {
            bgImage: brand.linearGradient(brand.gradientStops.cta),
            filter: "brightness(0.94)",
            _disabled: { filter: "none" },
          },
          _active: { filter: "brightness(0.88)" },
        },

        pagination: {
          border: "1px solid",
          borderColor: "gray.100",
          borderRadius: "md",
          backgroundColor: "transparent",
          _hover: { backgroundColor: "gray.50" },
          _dark: {
            borderColor: "#2C3230",
            _hover: { backgroundColor: "whiteAlpha.100" },
          },
        },
        // Pill toggles for quick filters (Favorites, Drafts…); `isActive`
        // marks the selected state.
        chip: {
          h: 8,
          px: 4,
          borderRadius: "full",
          border: "1px solid",
          borderColor: "gray.200",
          bg: "transparent",
          color: "gray.700",
          fontWeight: 500,
          _hover: { bg: "gray.50" },
          _active: {
            bg: "teal.50",
            borderColor: "teal.500",
            color: "teal.700",
          },
          _dark: {
            borderColor: "#2C3230",
            color: "gray.200",
            _hover: { bg: "whiteAlpha.100" },
            _active: {
              bg: "rgba(35, 164, 130, 0.16)",
              borderColor: "teal.500",
              color: "teal.300",
            },
          },
        },
        "outline-teal": {
          color: "brand.900",
          borderColor: "teal.500",
          borderStyle: "solid",
          borderWidth: "1px",
          backgroundColor: "white",
          _hover: {
            backgroundColor: "teal.50",
          },
          _dark: {
            color: "white",
            backgroundColor: "transparent",
            _hover: {
              backgroundColor: "rgba(35, 164, 130, 0.16)",
            },
          },
        },
      },
    },
    Modal: {
      sizes: {
        expandedCard: definePartsStyle({
          dialog: {
            maxWidth: "1024px",
            maxHeight: "768px",
            boxShadow: "none",
          },
        }),
        viewContainer: definePartsStyle({
          dialog: {
            maxWidth: "min(1800px, calc(100vw - 4rem))",
            maxHeight: "1200px",
            boxShadow: "none",
          },
        }),
      },
    },
    Tabs: {
      ...brandColorSchemeDefaults,
      variants: {
        corporate: ({ colorMode }: { colorMode: "light" | "dark" }) => ({
          tablist: {
            mt: "4",
            mr: "-4",
            height: "max-content",
            background: colorMode === "light" ? "gray.50" : "brand.400",
          },
          tab: {
            position: "relative",
            justifyContent: "flex-start",
            whiteSpace: "nowrap",
            fontSize: "sm",
            border: "1px solid",
            borderColor: colorMode === "light" ? "gray.200" : "gray.700",
            borderBottom: "unset",
            height: "40px",
            _hover: {
              borderRightColor: "transparent",
              background: colorMode === "light" ? "white" : "brand.375",
            },
            _last: {
              borderBottom: "1px solid",
              borderBottomColor:
                colorMode === "light" ? "gray.200" : "gray.700",
            },
            _before: {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "5px",
              background: "transparent",
            },
            _selected: {
              borderRightColor: "transparent",
              background: colorMode === "light" ? "white" : "brand.375",
              _before: {
                background: "teal.500",
              },
            },
          },
        }),
      },
    },
    // One calm table style: muted uppercase headers on a faint fill,
    // hairline row dividers, soft hover. Variants differ only in density.
    Table: {
      variants: {
        corporate: tableVariant("regular"),
        corporateCompact: tableVariant("compact"),
        intentsifyPlain: tableVariant("regular"),
        intentsifyStriped: tableVariant("regular"),
        intentsifyExpanded: tableVariant("regular"),
        intentsifyNew: tableVariant("compact"),
        intentsifyClean: tableVariant("regular", { flush: true }),
      },
    },
    Popover: {
      baseStyle: {
        popper: {
          width: "fit-content",
          maxWidth: "fit-content",
        },
      },
    },
  },
});

export const useComponentColors = () => ({
  form: {
    content: useColorModeValue("white", "brand.375"),
    stackBg: useColorModeValue("white", "brand.375"),
    boxBg: useColorModeValue("brand.300", "brand.500"),
    formLabelColor: useColorModeValue("gray.500", "gray.200"),
    formSubLabelColor: useColorModeValue("gray.500", "gray.200"),
  },
  spinner: {
    emptyColor: useColorModeValue("gray.100", "gray.500"),
    color: useColorModeValue("gray.500", "gray.100"),
  },
  popoverGray: useColorModeValue("gray.750", "gray.700"),
});
